import type { ZudokuConfig } from "zudoku";
import { BlocksIcon, BookMarkedIcon } from "zudoku/icons";
import {
  CheckpointTimeline,
  FeatureRow,
  FeatureTable,
  FlowArrow,
  FlowBox,
  FlowDiagram,
  FlowGroup,
  FlowRow,
  FlowStack,
  FlowStackItem,
  UseCaseCard,
  UseCaseCta,
  UseCaseGroup,
  ValueCard,
  ValueCards,
} from "./src/DiagramComponents";
import { Availability } from "./src/Availability";
import { topNavMenus } from "./src/TopNavMenus";
import { UseCaseCast, UseCaseVideo } from "./src/UseCaseMedia";

const config: ZudokuConfig = {
  metadata: {
    title: "%s | Unikraft Cloud Docs",
    description:
      "Unikraft Cloud documentation: guides, platform reference, CLI, and API docs for the millisecond, Linux-based microVM cloud platform.",
    favicon: "/favicon.ico",
  },
  basePath: "/docs",
  // Emit <link rel="canonical"> on every docs page (origin + basePath + route),
  // and generate a sitemap so docs pages are discoverable/indexable by agents.
  canonicalUrlOrigin: "https://unikraft.com",
  sitemap: {
    siteUrl: "https://unikraft.com",
  },
  site: {
    logo: {
      src: { light: "/logo-light.svg", dark: "/logo-dark.svg" },
      alt: "Zudoku",
      width: "130px",
    },
    showPoweredBy: false,
  },
  docs: {
    files: "/pages/**/*.{md,mdx}",
    defaultOptions: {
      showLastModified: true,
      suggestEdit: {
        text: "Edit this page",
        url: "https://github.com/unikraft-cloud/docs/edit/prod-stable/{filePath}",
      },
    },
    publishMarkdown: true,
    llms: {
      llmsTxt: true, // Generate llms.txt
      llmsTxtFull: true, // Generate llms-full.txt
      includeProtected: false, // Exclude protected routes
    },
  },
  mdx: {
    components: {
      Availability,
      CheckpointTimeline,
      FeatureRow,
      FeatureTable,
      FlowArrow,
      FlowBox,
      FlowDiagram,
      FlowGroup,
      FlowRow,
      FlowStack,
      FlowStackItem,
      UseCaseCard,
      UseCaseCast,
      UseCaseCta,
      UseCaseGroup,
      UseCaseVideo,
      ValueCard,
      ValueCards,
    },
  },
  syntaxHighlighting: {
    languages: [
      "c",
      "go",
      "html",
      "javascript",
      "json",
      "python",
      "rust",
      "shellscript",
      "typescript",
      "yaml",
    ],
    themes: {
      light: "github-light",
      dark: "github-dark-high-contrast",
    },
  },
  navigation: [
    {
      type: "category",
      label: "Documentation",
      icon: "book-open-text",
      items: [
        {
          type: "category",
          label: "Getting Started",
          icon: "star",
          collapsed: false,
          items: ["/introduction", "/faq"],
        },
        {
          type: "category",
          label: "Features",
          icon: "rocket",
          collapsed: false,
          items: [
            "/features/scale-to-zero",
            "/features/load-balancing",
            "/features/snapshots",
            "/features/autoscale",
            "/features/vertical-scaling",
            "/features/roms",
            "/features/autokill",
            "/features/on-demand-templates",
            "/features/cron-jobs",
            "/features/forking",
            "/features/branching",
            "/features/managed-volumes",
            "/features/checkpoints",
            "/features/live-migration",
            "/features/plugins",
            "/features/custom-network-configuration",
            "/features/network-shield",
            "/features/annotations",
          ],
        },
        {
          type: "category",
          label: "Cloud Platform",
          icon: "cloud",
          collapsed: false,
          items: [
            "/platform/metros",
            "/platform/hosting-options",
            "/platform/instances",
            "/platform/services",
            "/platform/networking",
            "/platform/domains",
            "/platform/certificates",
            "/platform/volumes",
            "/platform/images",
            "/platform/quotas",
            "/platform/metrics",
            "/platform/audit-events",
            "/platform/tagging",
            "/platform/delete-locks",
            "/platform/troubleshooting",
          ],
        },
        {
          type: "category",
          label: "Use Cases",
          icon: "lightbulb",
          collapsed: false,
          items: [
            "/use-cases/overview",
            "/use-cases/sandboxes",
            "/use-cases/headless-browsers",
            "/use-cases/mcp-servers",
            "/use-cases/api-gateways",
            "/use-cases/serverless-functions",
            "/use-cases/serverless-databases",
            "/use-cases/build-test-environments",
            "/use-cases/webhooks",
            "/use-cases/remote-ides",
            "/use-cases/game-servers",
            "/use-cases/remote-desktops",
          ],
        },
      ],
    },
    {
      type: "category",
      label: "Guides",
      icon: "graduation-cap",
      items: [
        "/guides/overview",
        {
          type: "category",
          label: "Tutorials",
          icon: "book",
          collapsed: false,
          items: [
            "/tutorials/docker-to-ukc",
            "/tutorials/kraftkit-to-unikraft",
            "/tutorials/environment-variables",
            "/tutorials/rootfs-formats",
            "/tutorials/rootfs-compression",
            "/tutorials/rootfs-volumes-roms",
            "/tutorials/scale-to-zero-triggers",
          ],
        },
        {
          type: "category",
          label: "Example apps",
          icon: "boxes",
          collapsed: false,
          items: [
            // AUTO-GENERATED:GUIDES-START
            "/guides/httpserver-dotnet10.0", // .NET HTTP Server
            "/guides/node18-agario", // Agar.io (Node)
            "/guides/tyk", // API Gateways: Tyk
            "/guides/build-environments", // Build and Test Environments with ROMs
            "/guides/httpserver-bun", // Bun HTTP Server
            "/guides/httpserver-gcc13.2", // C HTTP Server
            "/guides/httpserver-boost1.74-gpp13.2", // C++ Boost HTTP Server
            "/guides/httpserver-gpp13.2", // C++ HTTP Server
            "/guides/caddy2.7-go1.21", // Caddy
            "/guides/chromium-cdp", // Chromium CDP
            "/guides/chromium-cdp-auth", // Chromium CDP with Token Authentication
            "/guides/debian-ssh", // Debian SSH server
            "/guides/httpserver-python3.12-django5.0", // Django HTTP Server
            "/guides/dragonflydb", // DragonflyDB
            "/guides/duckdb-go1.21", // DuckDB with Go
            "/guides/httpserver-elixir1.16", // Elixir HTTP Server
            "/guides/httpserver-erlang26.2", // Erlang HTTP Server
            "/guides/httpserver-expressjs4.18-node21", // Express HTTP Server
            "/guides/httpserver-python3.12-fastapi-0.121.3", // FastAPI HTTP Server
            "/guides/httpserver-flask-redis", // Flask + Redis HTTP Server
            "/guides/httpserver-python3.12-flask3.0-sqlite", // Flask and SQLite HTTP Server
            "/guides/httpserver-python3.12-flask3.0", // Flask HTTP Server
            "/guides/nginx-flask-mongo", // Flask with MongoDB
            "/guides/minecraft", // Game Servers: Minecraft
            "/guides/httpserver-go1.22-redis", // Go and Redis HTTP Server
            "/guides/httpserver-go1.21", // Go HTTP Server
            "/guides/grafana", // Grafana
            "/guides/haproxy", // HAProxy
            "/guides/httpserver-node-express-puppeteer", // Headless Browsers: Puppeteer HTTP Server
            "/guides/hugo0.122", // Hugo
            "/guides/imaginary", // Imaginary
            "/guides/httpserver-java21", // Java HTTP Server
            "/guides/httpserver-lua5.1", // Lua HTTP Server
            "/guides/mariadb", // MariaDB
            "/guides/mcp-server-arxiv", // MCP Servers: ArXiv MCP Server
            "/guides/memcached1.6", // Memcached
            "/guides/minio", // Minio
            "/guides/mongodb", // MongoDB
            "/guides/mysql", // MySQL
            "/guides/neo4j", // Neo4j
            "/guides/httpserver-node21-nextjs", // Next.js HTTP Server
            "/guides/nginx", // Nginx
            "/guides/node24-karaoke", // Node AllKaraoke
            "/guides/httpserver-node26", // Node HTTP Server
            "/guides/node21-websocket", // Node WebSocket Server
            "/guides/opentelemetry-collector", // OpenTelemetry Collector
            "/guides/httpserver-perl5.42", // Perl HTTP Server
            "/guides/phoenix-postgres", // Phoenix with PostgreSQL
            "/guides/httpserver-php8.2", // PHP HTTP Server
            "/guides/node-playwright-chromium", // Playwright (Chromium) with Node.js
            "/guides/python-playwright-chromium", // Playwright (Chromium) with Python FastAPI
            "/guides/node-playwright-firefox", // Playwright (Firefox) with Node.js
            "/guides/node-playwright-webkit", // Playwright (WebKit) with Node.js
            "/guides/httpserver-prisma-expressjs4.19-node18", // Prisma HTTP Server
            "/guides/prometheus-grafana", // Prometheus and Grafana
            "/guides/httpserver-python3.12", // Python HTTP Server
            "/guides/httpserver-node22-react-router", // React Router HTTP Server
            "/guides/redis7.2", // Redis
            "/guides/novnc-browser", // Remote Desktops: noVNC
            "/guides/visual-studio-code-server", // Remote IDEs: Visual Studio Code Server
            "/guides/httpserver-ruby3.2", // Ruby HTTP Server
            "/guides/ruby3.2-rails", // Ruby on Rails
            "/guides/httpserver-rust1.88-actix-web4", // Rust (Actix Web) HTTP Server
            "/guides/httpserver-rust-trunkrs-leptos", // Rust (Leptos + Trunk) HTTP Server
            "/guides/httpserver-rust1.88-rocket0.5", // Rust (Rocket) HTTP Server
            "/guides/httpserver-rust1.75-tokio", // Rust (Tokio) HTTP Server
            "/guides/httpserver-rust1.91", // Rust HTTP Server
            "/guides/openclaw", // Sandboxes: OpenClaw
            "/guides/postgres", // Serverless Databases: PostgreSQL
            "/guides/node-code-execution", // Serverless Functions: Node.js Code Execution with ROMs
            "/guides/mcp-server-simple", // Simple MCP Server
            "/guides/skipper0.18", // Skipper
            "/guides/httpserver-node21-solid-start", // SolidJS HTTP Server
            "/guides/spin-wagi-http", // Spin
            "/guides/httpserver-java17-springboot", // Spring Boot HTTP Server
            "/guides/httpserver-java17-spring-petclinic", // Spring PetClinic
            "/guides/httpserver-c-debug", // SSH and HTTP Server with C and Debugging Tools
            "/guides/httpserver-node22-sveltekit", // SvelteKit HTTP Server
            "/guides/traefik", // Traefik
            "/guides/httpserver-node-vite-vanilla", // Vite (vanilla)
            "/guides/httpserver-node-vite-ssr-vanilla", // Vite (vanilla) SSR
            "/guides/httpserver-nginx-vite-vanilla", // Vite HTTP Server
            "/guides/vsftpd", // vsftpd
            "/guides/wazero-import-go", // Wazero
            "/guides/github-webhook-node", // Webhooks: GitHub Webhook Receiver
            "/guides/node18-wingsio", // Wings.io (Node)
            "/guides/wordpress-all-in-one", // Wordpress
            "/guides/wordpress", // Wordpress with Nginx and MariaDB
            // AUTO-GENERATED:GUIDES-END
          ],
        },
      ],
    },
    {
      type: "category",
      label: "SDKs",
      icon: "code",
      items: [
        "/sdks/overview",
        {
          type: "category",
          label: "Client libraries",
          icon: "library",
          collapsed: false,
          items: [
            {
              type: "category",
              label: "JavaScript SDK",
              icon: "code",
              collapsed: true,
              items: [
                "/sdks/js",
                {
                  type: "category",
                  label: "Sandboxes",
                  icon: "box",
                  collapsed: true,
                  items: [
                    "/sdks/js/sandboxes",
                    "/sdks/js/sandboxes/configuration",
                    "/sdks/js/sandboxes/commands",
                    "/sdks/js/sandboxes/files",
                    "/sdks/js/sandboxes/connect",
                  ],
                },
                "/sdks/js/client",
                "/sdks/js/metros",
                "/sdks/js/resources",
                "/sdks/js/errors",
              ],
            },
            "/sdks/python",
            "/sdks/go",
          ],
        },
        {
          type: "category",
          label: "Extending the platform",
          icon: "puzzle",
          collapsed: false,
          items: ["/sdks/plugin"],
        },
      ],
    },
    {
      type: "category",
      label: "CLI",
      icon: "terminal",
      items: [
        "/cli/overview",
        {
          type: "category",
          label: "Concepts",
          icon: "lightbulb",
          collapsed: false,
          items: ["/cli/registries", "/cli/fields"],
        },
        {
          type: "category",
          label: "unikraft",
          icon: "terminal",
          collapsed: false,
          items: [
            // AUTO-GENERATED:UNIKRAFT-CLI-START
            "/cli/unikraft",
            "/cli/unikraft/api",
            "/cli/unikraft/build",
            "/cli/unikraft/completion",
            "/cli/unikraft/login",
            "/cli/unikraft/logout",
            "/cli/unikraft/quotas",
            "/cli/unikraft/run",
            "/cli/unikraft/tui",
            "/cli/unikraft/upgrade",
            "/cli/unikraft/version",
            {
              type: "category",
              label: "unikraft certificates",
              icon: "shield-check",
              collapsed: false,
              items: [
                "/cli/unikraft/certificates",
                "/cli/unikraft/certificates/create",
                "/cli/unikraft/certificates/delete",
                "/cli/unikraft/certificates/get",
                "/cli/unikraft/certificates/list",
                "/cli/unikraft/certificates/wait",
              ],
            },
            {
              type: "category",
              label: "unikraft config",
              icon: "settings",
              collapsed: false,
              items: [
                "/cli/unikraft/config",
                "/cli/unikraft/config/get",
              ],
            },
            {
              type: "category",
              label: "unikraft images",
              icon: "package",
              collapsed: false,
              items: [
                "/cli/unikraft/images",
                "/cli/unikraft/images/build",
                "/cli/unikraft/images/copy",
                "/cli/unikraft/images/delete",
                "/cli/unikraft/images/get",
                "/cli/unikraft/images/list",
              ],
            },
            {
              type: "category",
              label: "unikraft instances",
              icon: "rocket",
              collapsed: false,
              items: [
                "/cli/unikraft/instances",
                "/cli/unikraft/instances/copy",
                "/cli/unikraft/instances/create",
                "/cli/unikraft/instances/delete",
                "/cli/unikraft/instances/edit",
                "/cli/unikraft/instances/exec",
                "/cli/unikraft/instances/get",
                "/cli/unikraft/instances/history",
                "/cli/unikraft/instances/list",
                "/cli/unikraft/instances/logs",
                "/cli/unikraft/instances/read",
                "/cli/unikraft/instances/restart",
                "/cli/unikraft/instances/run",
                "/cli/unikraft/instances/shell",
                "/cli/unikraft/instances/start",
                "/cli/unikraft/instances/stop",
                "/cli/unikraft/instances/suspend",
                "/cli/unikraft/instances/tunnel",
                "/cli/unikraft/instances/wait",
                "/cli/unikraft/instances/write",
                {
                  type: "category",
                  label: "unikraft instances checkpoint",
                  icon: "terminal",
                  collapsed: false,
                  items: [
                    "/cli/unikraft/instances/checkpoint",
                    "/cli/unikraft/instances/checkpoint/create",
                    "/cli/unikraft/instances/checkpoint/delete",
                    "/cli/unikraft/instances/checkpoint/edit",
                    "/cli/unikraft/instances/checkpoint/get",
                    "/cli/unikraft/instances/checkpoint/history",
                    "/cli/unikraft/instances/checkpoint/list",
                    "/cli/unikraft/instances/checkpoint/wait",
                  ],
                },
                {
                  type: "category",
                  label: "unikraft instances template",
                  icon: "terminal",
                  collapsed: false,
                  items: [
                    "/cli/unikraft/instances/template",
                    "/cli/unikraft/instances/template/create",
                    "/cli/unikraft/instances/template/delete",
                    "/cli/unikraft/instances/template/edit",
                    "/cli/unikraft/instances/template/get",
                    "/cli/unikraft/instances/template/list",
                  ],
                },
              ],
            },
            {
              type: "category",
              label: "unikraft metros",
              icon: "earth",
              collapsed: false,
              items: [
                "/cli/unikraft/metros",
                "/cli/unikraft/metros/get",
                "/cli/unikraft/metros/list",
              ],
            },
            {
              type: "category",
              label: "unikraft profile",
              icon: "user-circle",
              collapsed: false,
              items: [
                "/cli/unikraft/profile",
                "/cli/unikraft/profile/get",
                "/cli/unikraft/profile/list",
                "/cli/unikraft/profile/use",
              ],
            },
            {
              type: "category",
              label: "unikraft services",
              icon: "split",
              collapsed: false,
              items: [
                "/cli/unikraft/services",
                "/cli/unikraft/services/create",
                "/cli/unikraft/services/delete",
                "/cli/unikraft/services/edit",
                "/cli/unikraft/services/get",
                "/cli/unikraft/services/list",
                "/cli/unikraft/services/wait",
              ],
            },
            {
              type: "category",
              label: "unikraft volumes",
              icon: "cylinder",
              collapsed: false,
              items: [
                "/cli/unikraft/volumes",
                "/cli/unikraft/volumes/attach",
                "/cli/unikraft/volumes/clone",
                "/cli/unikraft/volumes/create",
                "/cli/unikraft/volumes/delete",
                "/cli/unikraft/volumes/detach",
                "/cli/unikraft/volumes/edit",
                "/cli/unikraft/volumes/get",
                "/cli/unikraft/volumes/import",
                "/cli/unikraft/volumes/list",
                "/cli/unikraft/volumes/wait",
                {
                  type: "category",
                  label: "unikraft volumes template",
                  icon: "terminal",
                  collapsed: false,
                  items: [
                    "/cli/unikraft/volumes/template",
                    "/cli/unikraft/volumes/template/create",
                    "/cli/unikraft/volumes/template/delete",
                    "/cli/unikraft/volumes/template/edit",
                    "/cli/unikraft/volumes/template/get",
                    "/cli/unikraft/volumes/template/list",
                  ],
                },
              ],
            },
            // AUTO-GENERATED:UNIKRAFT-CLI-END
          ],
        },
      ],
    } as any,
    {
      type: "link",
      label: "API",
      icon: "unplug",
      to: "/api/platform/v1",
    },
    "/integrations/kubernetes",
    {
      type: "link",
      label: "Docker",
      icon: "package",
      to: "/tutorials/docker-to-ukc",
    },
    {
      type: "link",
      label: "Terraform",
      icon: "layers",
      to: "https://github.com/unikraft-cloud/terraform-provider-ukc",
    },
    "/integrations/karpenter",
    {
      type: "category",
      label: "Kraftfile",
      icon: "file-text",
      items: [
        "/kraftfile/v0.7",
        {
          type: "link",
          label: "Kraftfile Reference (v0.6)",
          to: "https://unikraft.org/docs/cli/reference/kraftfile/v0.6",
        },
        {
          type: "link",
          label: "Kraftfile Reference (v0.5)",
          to: "https://unikraft.org/docs/cli/reference/kraftfile/v0.5",
        },
      ],
    } as any,
    {
      type: "category",
      label: "Releases",
      icon: "tag",
      items: [
        "/releases/index",
        "/releases/r15-isonoe",
        "/releases/r14-harpalyke",
        "/releases/r13-adrastea",
        "/releases/r12-thebe",
        "/releases/r11-metis",
        "/releases/r10-themisto",
        "/releases/r9-leda",
        "/releases/r8-ananke",
        "/releases/r7-carme",
        "/releases/r6-lysithea",
        "/releases/r5-amalthea",
        "/releases/r4-europa",
        "/releases/r3-io",
        "/releases/r2-callisto",
        "/releases/r1-ganymede",
      ],
    },
  ],
  slots: {
    "top-navigation-side": topNavMenus([
      {
        label: "Integrations",
        icon: BlocksIcon,
        tabs: ["Kubernetes", "Docker", "Terraform", "Karpenter"],
      },
      {
        label: "Reference",
        icon: BookMarkedIcon,
        tabs: ["Kraftfile"],
      },
    ]),
  },
  search: {
    type: "pagefind",
  },
  redirects: [
    { from: "/", to: "/introduction" },
    { from: "/cli", to: "/cli/overview" },
    { from: "/kraftfile", to: "/kraftfile/v0.7" },
    { from: "/guides", to: "/guides/overview" },
    { from: "/use-cases", to: "/use-cases/overview" },
    { from: "/releases", to: "/releases/index" },
    { from: "/sdks", to: "/sdks/overview" },
    { from: "/integrations", to: "/integrations/kubernetes" },
    // The SDKs used to live under Integrations.
    { from: "/integrations/sdks", to: "/sdks/overview" },
    { from: "/integrations/sdks/go", to: "/sdks/go" },
    { from: "/integrations/sdks/js", to: "/sdks/js" },
    { from: "/integrations/sdks/plugin", to: "/sdks/plugin" },
    // The removed `kraft cloud` CLI reference used to live under /cli/kraft.
    { from: "/cli/legacy-overview", to: "/cli/overview" },
    { from: "/cli/kraft", to: "/cli/overview" },
    { from: "/cli/kraft/overview", to: "/cli/overview" },
    { from: "/cli/kraft/cert", to: "/cli/unikraft/certificates" },
    { from: "/cli/kraft/cert/create", to: "/cli/unikraft/certificates/create" },
    { from: "/cli/kraft/cert/get", to: "/cli/unikraft/certificates/get" },
    { from: "/cli/kraft/cert/list", to: "/cli/unikraft/certificates/list" },
    { from: "/cli/kraft/cert/remove", to: "/cli/unikraft/certificates/delete" },
    { from: "/cli/kraft/compose", to: "/cli/overview" },
    { from: "/cli/kraft/compose/build", to: "/cli/overview" },
    { from: "/cli/kraft/compose/create", to: "/cli/overview" },
    { from: "/cli/kraft/compose/down", to: "/cli/overview" },
    { from: "/cli/kraft/compose/log", to: "/cli/overview" },
    { from: "/cli/kraft/compose/ls", to: "/cli/overview" },
    { from: "/cli/kraft/compose/ps", to: "/cli/overview" },
    { from: "/cli/kraft/compose/push", to: "/cli/overview" },
    { from: "/cli/kraft/compose/start", to: "/cli/overview" },
    { from: "/cli/kraft/compose/stop", to: "/cli/overview" },
    { from: "/cli/kraft/compose/up", to: "/cli/overview" },
    { from: "/cli/kraft/deploy", to: "/cli/unikraft/run" },
    { from: "/cli/kraft/image", to: "/cli/unikraft/images" },
    { from: "/cli/kraft/image/list", to: "/cli/unikraft/images/list" },
    { from: "/cli/kraft/image/remove", to: "/cli/unikraft/images/delete" },
    { from: "/cli/kraft/instance", to: "/cli/unikraft/instances" },
    { from: "/cli/kraft/instance/create", to: "/cli/unikraft/instances/create" },
    { from: "/cli/kraft/instance/get", to: "/cli/unikraft/instances/get" },
    { from: "/cli/kraft/instance/list", to: "/cli/unikraft/instances/list" },
    { from: "/cli/kraft/instance/logs", to: "/cli/unikraft/instances/logs" },
    { from: "/cli/kraft/instance/restart", to: "/cli/unikraft/instances/restart" },
    { from: "/cli/kraft/instance/start", to: "/cli/unikraft/instances/start" },
    { from: "/cli/kraft/instance/stop", to: "/cli/unikraft/instances/stop" },
    { from: "/cli/kraft/instance/remove", to: "/cli/unikraft/instances/delete" },
    { from: "/cli/kraft/instance/template", to: "/cli/unikraft/instances/template" },
    { from: "/cli/kraft/instance/template/create", to: "/cli/unikraft/instances/template/create" },
    { from: "/cli/kraft/instance/template/get", to: "/cli/unikraft/instances/template/get" },
    { from: "/cli/kraft/instance/template/list", to: "/cli/unikraft/instances/template/list" },
    { from: "/cli/kraft/instance/template/remove", to: "/cli/unikraft/instances/template/delete" },
    { from: "/cli/kraft/metro", to: "/cli/unikraft/metros" },
    { from: "/cli/kraft/metro/list", to: "/cli/unikraft/metros/list" },
    { from: "/cli/kraft/quota", to: "/cli/unikraft/quotas" },
    { from: "/cli/kraft/scale", to: "/features/autoscale" },
    { from: "/cli/kraft/scale/add", to: "/features/autoscale" },
    { from: "/cli/kraft/scale/get", to: "/features/autoscale" },
    { from: "/cli/kraft/scale/init", to: "/features/autoscale" },
    { from: "/cli/kraft/scale/remove", to: "/features/autoscale" },
    { from: "/cli/kraft/scale/reset", to: "/features/autoscale" },
    { from: "/cli/kraft/service", to: "/cli/unikraft/services" },
    { from: "/cli/kraft/service/create", to: "/cli/unikraft/services/create" },
    { from: "/cli/kraft/service/get", to: "/cli/unikraft/services/get" },
    { from: "/cli/kraft/service/list", to: "/cli/unikraft/services/list" },
    { from: "/cli/kraft/service/remove", to: "/cli/unikraft/services/delete" },
    { from: "/cli/kraft/service/drain", to: "/cli/unikraft/services" },
    { from: "/cli/kraft/service/logs", to: "/cli/unikraft/services" },
    { from: "/cli/kraft/tunnel", to: "/cli/unikraft/instances/tunnel" },
    { from: "/cli/kraft/volume", to: "/cli/unikraft/volumes" },
    { from: "/cli/kraft/volume/attach", to: "/cli/unikraft/volumes/attach" },
    { from: "/cli/kraft/volume/create", to: "/cli/unikraft/volumes/create" },
    { from: "/cli/kraft/volume/detach", to: "/cli/unikraft/volumes/detach" },
    { from: "/cli/kraft/volume/get", to: "/cli/unikraft/volumes/get" },
    { from: "/cli/kraft/volume/import", to: "/cli/unikraft/volumes/import" },
    { from: "/cli/kraft/volume/list", to: "/cli/unikraft/volumes/list" },
    { from: "/cli/kraft/volume/remove", to: "/cli/unikraft/volumes/delete" },
    { from: "/cli/kraft/volume/template", to: "/cli/unikraft/volumes/template" },
    { from: "/cli/kraft/volume/template/create", to: "/cli/unikraft/volumes/template/create" },
    { from: "/cli/kraft/volume/template/get", to: "/cli/unikraft/volumes/template/get" },
    { from: "/cli/kraft/volume/template/list", to: "/cli/unikraft/volumes/template/list" },
    { from: "/cli/kraft/volume/template/remove", to: "/cli/unikraft/volumes/template/delete" },
  ],
  apis: [
    {
      type: "file",
      input: "./apis/platform.yaml",
      path: "/api/platform/v1",
    },
  ],
  theme: {
    customCss: `
      .prose td code {
        white-space: normal;
        overflow-wrap: normal;
      }

      .prose header h1 {
        margin-bottom: 1rem;
      }

      .prose .not-prose h2 {
        margin: 0;
      }

      .prose > .h-16 {
        height: 1rem;
      }
    `,
    fonts: {
      sans: {
        fontFamily: "Inter, sans-serif",
        url: "https://fonts.googleapis.com/css2?family=Inter:wght@400;700&display=swap",
      },
      mono: {
        fontFamily: "IBM Plex Mono, monospace",
        url: "https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;700&display=swap",
      },
    },
    light: {
      background: "#ffffff", // Main background color
      foreground: "#020817", // Main text color
      card: "#ffffff", // Card background color
      cardForeground: "#020817", // Card text color
      popover: "#ffffff", // Popover background color
      popoverForeground: "#020817", // Popover text color
      primary: "#2563eb", // Primary action color
      primaryForeground: "#ffffff", // Text color on primary backgrounds
      secondary: "#f1f5f9", // Secondary action color
      secondaryForeground: "#020817", // Text color on secondary backgrounds
      muted: "#f1f5f9", // Muted/subtle background color
      mutedForeground: "#64748b", // Text color for muted elements
      accent: "#f1f5f9", // Accent color for highlights
      accentForeground: "#020817", // Text color on accent backgrounds
      destructive: "#ef4444", // Color for destructive actions
      destructiveForeground: "#ffffff", // Text color on destructive backgrounds
      border: "#cbd5e1", // Border color
      input: "#e2e8f0", // Input field border color
      ring: "#0284c7", // Focus ring color
      radius: "0.4rem", // Border radius value
    },
    dark: {
      background: "#000000", // Main background color
      foreground: "#dbeafe", // Main text color
      card: "#000000", // Card background color
      cardForeground: "#dbeafe", // Card text color
      popover: "hsl(20 14.3% 4.1%)", // Popover background color
      popoverForeground: "hsl(60 9.1% 97.8%)", // Popover text color
      primary: "#2563eb", // Primary action color
      primaryForeground: "#ffffff", // Text color on primary backgrounds
      secondary: "var(--color-slate-800)", // Secondary action color
      secondaryForeground: "hsl(60 9.1% 97.8%)", // Text color on secondary backgrounds
      muted: "var(--color-slate-900)", // Muted/subtle background color
      mutedForeground: "var(--color-slate-600)", // Text color for muted elements
      accent: "var(--color-slate-800)", // Accent color for highlights
      accentForeground: "hsl(60 9.1% 97.8%)", // Text color on accent backgrounds
      destructive: "var(--color-rose-800)", // Color for destructive actions
      destructiveForeground: "hsl(60 9.1% 97.8%)", // Text color on destructive backgrounds
      border: "#1e293b", // Border color
      input: "#334155", // Input field border color
      ring: "var(--color-amber-500)", // Focus ring color
      radius: "0.4rem", // Border radius value
    },
  },
};

export default config;
